// src/components/Layout.jsx
import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, LogOut, Sun, Moon, Settings } from 'lucide-react';
import RouteErrorBoundary from './RouteErrorBoundary';
import MoraLogo from './MoraLogo';
import { useApp } from '../context/AppContext';
import { useTeamSettings } from '../context/TeamSettingsContext';
import { appStyles } from '../utils/styles';
import MobileNav from './MobileNav';
import AIChatToggle from './AIChatToggle';
import AIChatPanel from './AIChatPanel';
import useTheme from '../hooks/useTheme';
import { useWeeklyReviews } from '../hooks/useWeeklyReviews';
import { useApprovals } from '../hooks/useApprovals';

const NewProjectSelector = React.lazy(() => import('../modals/NewProjectSelector'));
const PropertyLookupModal = React.lazy(() => import('../modals/PropertyLookupModal'));
const AddNewLeadModal = React.lazy(() => import('../modals/AddNewLeadModal'));
const OnboardingWizard = React.lazy(() => import('./OnboardingWizard'));

export default function Layout() {
  const {
    currentUser,
    handleLogout,
    navItems,
    showNewProjectModal,
    showPropertyLookup,
    setShowPropertyLookup,
    showAddNewLead,
    setShowAddNewLead,
    userProfile,
    ownerProfile,
    teamOwnerId,
    firebaseUser,
    projects,
    updateProjectInFirebase,
    addNotification,
    showOnboarding,
    completeOnboarding,
    dismissOnboarding,
    canUseFeature,
  } = useApp();

  const { isFeatureEnabled } = useTeamSettings();
  const { isDark, toggleTheme } = useTheme();
  const { pendingCount: approvalCount } = useApprovals();

  // Phase 9: Auto-generate weekly review tasks for rehab projects
  useWeeklyReviews({ projects, updateProjectInFirebase, addNotification, isFeatureEnabled });

  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarExpanded, setSidebarExpanded] = useState({
    acquisition: false,
    rehab: false,
    selling: false,
  });

  // Check if a path is active (boundary-aware to avoid prefix collisions like /rehab vs /rehab-pipeline)
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    // Exact match for phase dashboard paths to prevent /selling highlighting on /selling/listings
    if (path === '/selling' || path === '/rehab' || path === '/acquisition') {
      return location.pathname === path;
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // User initials for avatar
  const userName = currentUser?.name || currentUser?.displayName || 'User';
  const initials = userName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div
      className="min-h-screen bg-slate-50 dark:bg-slate-900"
      style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      <style>{appStyles}</style>

      {/* Skip to main content — accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-[9999] focus:top-4 focus:left-4 focus:px-4 focus:py-2 focus:bg-white focus:text-blue-600 focus:rounded-lg focus:shadow-lg focus:outline-none"
      >
        Skip to main content
      </a>

      {/* Sidebar — hidden on mobile, visible md+ */}
      <aside
        data-tutorial="sidebar"
        aria-label="Sidebar navigation"
        className="sidebar fixed left-0 top-0 h-full w-64 flex-col z-50 overflow-y-auto hidden md:flex"
      >
        {/* Logo */}
        <div className="p-6 border-b border-slate-200 dark:border-slate-700">
          <MoraLogo />
        </div>

        <nav aria-label="Main navigation" className="flex-1 p-4">
          <p className="text-[11px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-3 px-3">
            Menu
          </p>
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.id}>
                {item.children ? (
                  <div
                    data-tutorial={
                      item.id === 'acquisition'
                        ? 'acquisition-menu'
                        : item.id === 'rehab'
                          ? 'rehab-menu'
                          : item.id === 'selling'
                            ? 'selling-menu'
                            : undefined
                    }
                  >
                    <button
                      onClick={() =>
                        setSidebarExpanded((prev) => ({ ...prev, [item.id]: !prev[item.id] }))
                      }
                      className="nav-item w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100"
                    >
                      <div className="flex items-center gap-3">
                        <item.icon
                          size={20}
                          className="nav-icon text-slate-400 dark:text-slate-500"
                        />
                        {item.label}
                      </div>
                      <ChevronDown
                        size={16}
                        className={`text-slate-400 dark:text-slate-500 transition-transform ${sidebarExpanded[item.id] ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {sidebarExpanded[item.id] && (
                      <ul className="ml-4 mt-1 space-y-1">
                        {item.children.map((child) => (
                          <li key={child.id}>
                            <button
                              onClick={() => navigate(child.path)}
                              className={`nav-item w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium ${
                                isActive(child.path)
                                  ? 'active'
                                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                              }`}
                            >
                              <child.icon
                                size={16}
                                className={`nav-icon ${isActive(child.path) ? '' : 'text-slate-400 dark:text-slate-500'}`}
                              />
                              {child.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => navigate(item.path)}
                    data-tutorial={item.id === 'settings' ? 'settings-link' : undefined}
                    className={`nav-item w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium ${
                      isActive(item.path)
                        ? 'active'
                        : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100'
                    }`}
                  >
                    <item.icon
                      size={20}
                      className={`nav-icon ${isActive(item.path) ? '' : 'text-slate-400 dark:text-slate-500'}`}
                    />
                    {item.label}
                    {/* Approvals badge */}
                    {item.badge && approvalCount > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5">
                        {approvalCount}
                      </span>
                    )}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* Profile Footer */}
        <div className="border-t border-slate-200 dark:border-slate-700">
          {/* User row */}
          <div className="flex items-center gap-3 px-5 py-4">
            <div className="w-9 h-9 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-sm font-bold">{initials}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
                {userName}
              </p>
              <p className="text-[11px] text-slate-400 dark:text-slate-500 font-medium">Admin</p>
            </div>
          </div>

          {/* Utility grid */}
          <div className="grid grid-cols-3 border-t border-slate-100 dark:border-slate-700/50">
            <button
              onClick={toggleTheme}
              className="flex flex-col items-center gap-1 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              title={isDark ? 'Light mode' : 'Dark mode'}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? (
                <Sun size={16} className="text-slate-400" />
              ) : (
                <Moon size={16} className="text-slate-400" />
              )}
              <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">
                {isDark ? 'Light' : 'Dark'}
              </span>
            </button>
            <button
              onClick={() => navigate('/settings')}
              className="flex flex-col items-center gap-1 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              title="Settings"
              aria-label="Settings"
            >
              <Settings size={16} className="text-slate-400" />
              <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">
                Settings
              </span>
            </button>
            <button
              onClick={handleLogout}
              className="flex flex-col items-center gap-1 py-3 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              title="Log Out"
              aria-label="Log Out"
            >
              <LogOut size={16} className="text-slate-400" />
              <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">
                Logout
              </span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main id="main-content" className="ml-0 md:ml-64 min-h-screen pb-16 md:pb-0">
        <RouteErrorBoundary>
          <Outlet />
        </RouteErrorBoundary>
      </main>

      {/* Modals */}
      {showNewProjectModal && (
        <React.Suspense fallback={null}>
          <NewProjectSelector />
        </React.Suspense>
      )}
      {showPropertyLookup && (
        <React.Suspense fallback={null}>
          <PropertyLookupModal onClose={() => setShowPropertyLookup(false)} />
        </React.Suspense>
      )}
      {showAddNewLead && (
        <React.Suspense fallback={null}>
          <AddNewLeadModal onClose={() => setShowAddNewLead(false)} />
        </React.Suspense>
      )}

      {/* Mobile bottom nav */}
      <MobileNav />

      {/* AI Chat Assistant (Phase 3) */}
      {canUseFeature && canUseFeature('aiAssistant') && (
        <>
          <AIChatToggle />
          <AIChatPanel />
        </>
      )}

      {/* First-run onboarding */}
      {showOnboarding && (
        <React.Suspense fallback={null}>
          <OnboardingWizard onComplete={completeOnboarding} onDismiss={dismissOnboarding} />
        </React.Suspense>
      )}
    </div>
  );
}
